import { Location } from '@angular/common';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { MaintenanceActivitiesModel } from 'src/app/models/maintenance/maintenance-activities-model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { MaintenanceActivitiesService } from 'src/app/services/maintenance/maintenance-activities.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-maintenance-activities',
  templateUrl: './register-maintenance-activities.component.html',
  styleUrls: ['./register-maintenance-activities.component.scss'],
  standalone: false
})
export class RegisterMaintenanceActivitiesComponent implements OnInit {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly location = inject(Location);
  private readonly maintenanceActivitiesService = inject(MaintenanceActivitiesService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  disabledForm: boolean = false;
  formMaintenanceActivities: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  nameWindow: string = 'Captura Actividades de Mantenimiento';
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  maintenanceActivitiesContract: MaintenanceActivitiesModel = new MaintenanceActivitiesModel();
  maintenanceActivitiesId: number = 0;
  titleState: string = '';
  url: string = 'maintenance-activities';

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.activatedRoute.params.subscribe(params => {
      this.maintenanceActivitiesId = Object.keys(params).length > 0 ? params?.id : null;
    });
    this.validateForm(this.maintenanceActivitiesContract);
    await this.getPermission();
    this.handleInitialState();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeCode(code: number) {
    if (!code || this.maintenanceActivitiesContract.code === code) { return; }
    try {
      const response = await firstValueFrom(this.maintenanceActivitiesService.getMaintenanceActivityByCode(code));
      this.maintenanceActivitiesId = response.maintenanceActivitiesId;
      this.maintenanceActivitiesContract = response;
      this.validateForm(this.maintenanceActivitiesContract);
      this.location.replaceState(`/${this.url}/register/${this.maintenanceActivitiesId}`);
    } catch (error) {
      console.error('Error al buscar la actividad de mantenimiento por código.', error);
    }
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para crear actividades de mantenimiento.
          Comuníquese con el administrador del sistema.`);
      return;
    }
    this.maintenanceActivitiesId = 0;
    this.maintenanceActivitiesContract = new MaintenanceActivitiesModel();
    this.validateForm(this.maintenanceActivitiesContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  async delete() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `El usuario no tiene permisos para eliminar actividades de mantenimiento.
          Comuníquese con el administrador del sistema.`);
      return;
    }
    if (!this.maintenanceActivitiesId || this.maintenanceActivitiesId === 0) {
      this.toastHandlerService.showToast('warning', 'Debe crear una actividad de mantenimiento.');
      return;
    }

    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar la actividad de mantenimiento?');
    if (accepted) {
      try {
        await firstValueFrom(this.maintenanceActivitiesService.deleteMaintenanceActivities(this.maintenanceActivitiesContract.maintenanceActivitiesId));
        this.createNewRegister();
        this.toastHandlerService.showToast('success', 'La actividad de mantenimiento fue eliminada exitosamente.');
      } catch (error) {
        console.error('Error al eliminar la actividad de mantenimiento.', error);
      }
    }
  }

  async getButtonClick(buttonClick: any) {
    const code = this.formMaintenanceActivities.get("code")?.value;
    this.handleMaintenanceActivitiesNavigation(buttonClick, code)
  }


  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
    await Promise.all([
      this.getPermissionProfileByUrl(),
    ]);
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getMaintenanceActivityById() {
    try {
      const response = await firstValueFrom(this.maintenanceActivitiesService.getMaintenanceActivityById(this.maintenanceActivitiesId));
      this.maintenanceActivitiesContract = response;
      this.validateForm(response);
      this.location.replaceState(`/${this.url}/register/${this.maintenanceActivitiesId}`);
    } catch (error) {
      console.error('Error al obtener la actividad de mantenimiento por id.', error);
    }
  }

  async handleInitialState() {
    if (this.maintenanceActivitiesId !== null && this.maintenanceActivitiesId !== 0) {
      await this.getMaintenanceActivityById();
    } else {
      this.createNewRegister();
    }
  }

  async handleMaintenanceActivitiesNavigation(buttonClick: any, code: number) {
    const actionMap: Record<string, () => Promise<MaintenanceActivitiesModel>> = {
      first: () => firstValueFrom(this.maintenanceActivitiesService.getFirstMaintenanceActivitiesByKeysAsync()),
      previus: () => firstValueFrom(this.maintenanceActivitiesService.getPreviusMaintenanceActivitiesByKeysAsync(code)),
      next: () => firstValueFrom(this.maintenanceActivitiesService.getNextMaintenanceActivitiesByKeysAsync(code)),
      last: () => firstValueFrom(this.maintenanceActivitiesService.getLastMaintenanceActivitiesByKeysAsync()),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.maintenanceActivitiesId = 0;
      this.maintenanceActivitiesContract = new MaintenanceActivitiesModel();
      return;
    }
    try {
      const maintenanceActivities = await action();
      this.maintenanceActivitiesId = maintenanceActivities.maintenanceActivitiesId;
      this.maintenanceActivitiesContract = maintenanceActivities;
      this.validateForm(this.maintenanceActivitiesContract);
      this.location.replaceState(`/${this.url}/register/${this.maintenanceActivitiesId}`);
    } catch (error) {
      this.maintenanceActivitiesContract = new MaintenanceActivitiesModel();
      this.maintenanceActivitiesContract.maintenanceActivitiesId = 0;
      console.error(`Error al buscar la actividad de mantenimiento (${buttonClick.target.id}).`, error);
    }
  }

  async send() {
    this.validateFormControls();
    if (this.formMaintenanceActivities.valid) {
      const modelMaintenanceActivities = this.formMaintenanceActivities.getRawValue() as MaintenanceActivitiesModel;
      if (modelMaintenanceActivities.alterCode == "") modelMaintenanceActivities.alterCode = null;
      if (this.maintenanceActivitiesId == 0) {
        try {
          const response = await firstValueFrom(this.maintenanceActivitiesService.createMaintenanceActivities(modelMaintenanceActivities));
          this.maintenanceActivitiesId = response.maintenanceActivitiesId;
          this.maintenanceActivitiesContract = response;
          this.validateForm(this.maintenanceActivitiesContract);
          this.location.replaceState(`/${this.url}/register/${this.maintenanceActivitiesId}`);
          this.toastHandlerService.showToast('success', `La actividad de mantenimiento fue creada exitosamente.`);
        } catch (error) {
          console.error('Error al crear la actividad de mantenimiento.', error);
        }
      } else {
        this.maintenanceActivitiesContract.alterCode = modelMaintenanceActivities.alterCode;
        this.maintenanceActivitiesContract.description = modelMaintenanceActivities.description;
        try {
          const response = await firstValueFrom(this.maintenanceActivitiesService.updateMaintenanceActivities(this.maintenanceActivitiesContract));
          this.maintenanceActivitiesContract = response;
          this.validateForm(this.maintenanceActivitiesContract);
          this.toastHandlerService.showToast('success', `La actividad de mantenimiento fue modificada exitosamente.`);
        } catch (error) {
          console.error('Error al actualizar la actividad de mantenimiento.', error);
        }
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages(): void {
    const controlMessages: Record<string, Record<string, string>> = {
      alterCode: {
        maxlength: 'El código alterno no puede tener más de 20 caracteres.'
      },
      description: {
        required: 'El campo descripción es obligatorio.',
        maxlength: 'El descripción no puede tener más de 80 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formMaintenanceActivities.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm(maintenanceActivities: MaintenanceActivitiesModel) {
    this.formMaintenanceActivities = this.formBuilder.group({
      code: [{ value: maintenanceActivities.code, disabled: this.maintenanceActivitiesId === 0 }, [Validators.required]],
      alterCode: [maintenanceActivities.alterCode, [Validators.maxLength(20)]],
      description: [maintenanceActivities.description, [Validators.required, Validators.maxLength(80)]],
    });
  }

  validateFormControls() {
    Object.keys(this.formMaintenanceActivities.controls).forEach((key) => {
      const control = this.formMaintenanceActivities.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
